import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBell,
  faXmark,
  faCircleArrowUp,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'

import {
  isPushSupported,
  isIOS,
  isStandalone,
  currentPermission,
  enablePush,
  getPreferences,
} from '../lib/pushNotifications'

const DISMISS_KEY = 'cc_notif_banner_dismissed_at'
const DISMISS_DAYS = 7

function isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const ts = Date.parse(raw)
    if (!Number.isFinite(ts)) return false
    const ageMs = Date.now() - ts
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000
  } catch (e) {
    return false
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString())
  } catch (e) {
    /* noop */
  }
}

// Banner CTA che appare sotto la navbar consumer quando l'utente non ha
// ancora attivato le notifiche. Una volta dismissato, ricompare dopo 7
// giorni; una volta attivato, non ricompare più (lo stato vive nel DB).
//
// Tre varianti:
//  - default: utente loggato + push supportato + (Android/desktop o iOS
//    standalone) → bottone "Attiva".
//  - ios-add-home: iOS + non standalone → istruzioni "rimuovi e riaggiungi
//    a Home". NESSUN bottone Attiva (servirebbe uscire prima dalla
//    Safari-non-standalone).
//  - permission-blocked: permission === 'denied' → istruzioni per sbloccare.
function NotificationsCTA({ user }) {
  const [hidden, setHidden] = useState(false)
  const [variant, setVariant] = useState(null) // 'default' | 'ios' | 'blocked'
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    if (!user) {
      setVariant(null)
      return undefined
    }
    if (!isPushSupported()) {
      setVariant(null)
      return undefined
    }
    if (isRecentlyDismissed()) {
      setVariant(null)
      return undefined
    }

    const perm = currentPermission()
    if (perm === 'denied') {
      setVariant('blocked')
      return undefined
    }

    if (isIOS() && !isStandalone()) {
      setVariant('ios')
      return undefined
    }

    // Caso default: serve sapere dal server se l'utente ha già attivato.
    ;(async () => {
      const prefs = await getPreferences()
      if (cancelled) return
      if (prefs && (prefs.enabled || prefs.subscriptionsCount > 0)) {
        setVariant(null)
        return
      }
      if (prefs && prefs.pushConfigured === false) {
        setVariant(null) // server non configurato: non rompere le balls
        return
      }
      setVariant('default')
    })()

    return () => {
      cancelled = true
    }
  }, [user])

  if (hidden || !variant) return null

  const onDismiss = () => {
    markDismissed()
    setHidden(true)
  }

  const onActivate = async () => {
    if (busy) return
    setBusy(true)
    setError('')
    const r = await enablePush()
    setBusy(false)
    if (r.ok) {
      setHidden(true)
      return
    }
    if (r.error === 'permission_denied') {
      setVariant('blocked')
      return
    }
    if (r.error === 'unsupported') {
      setHidden(true)
      return
    }
    setError(
      r.error === 'vapid_not_configured'
        ? 'Il servizio notifiche non è ancora configurato.'
        : 'Attivazione non riuscita. Riprova fra un istante.',
    )
  }

  return (
    <div className={'notifications-cta notifications-cta-' + variant}>
      <button
        type="button"
        className="notifications-cta-close"
        onClick={onDismiss}
        aria-label="Chiudi"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>

      <div className="notifications-cta-icon">
        <FontAwesomeIcon
          icon={
            variant === 'blocked'
              ? faTriangleExclamation
              : variant === 'ios'
              ? faCircleArrowUp
              : faBell
          }
        />
      </div>

      <div className="notifications-cta-body">
        {variant === 'default' && (
          <>
            <div className="notifications-cta-title">Promemoria pranzo</div>
            <div className="notifications-cta-text">
              Ricevi una notifica 15 minuti prima della chiusura ordini, così
              non te ne dimentichi.
            </div>
            {error && <div className="notifications-cta-error">{error}</div>}
            <div className="notifications-cta-actions">
              <button
                type="button"
                className="notifications-cta-btn"
                onClick={onActivate}
                disabled={busy}
              >
                {busy ? 'Attivazione…' : 'Attiva'}
              </button>
            </div>
          </>
        )}

        {variant === 'ios' && (
          <>
            <div className="notifications-cta-title">
              Attiva i promemoria su iPhone
            </div>
            <div className="notifications-cta-text">
              Per ricevere le notifiche su iPhone serve aprire l'app dalla
              schermata Home:
            </div>
            <ol className="notifications-cta-steps">
              <li>
                Se hai già un'icona CUT sulla Home, <b>rimuovila</b> (tieni
                premuto → Rimuovi).
              </li>
              <li>
                Apri questa pagina in <b>Safari</b>.
              </li>
              <li>
                Tocca <b>Condividi</b> ⬆ → <b>Aggiungi alla schermata Home</b>.
              </li>
              <li>
                Apri l'app dalla nuova icona Home: torna qui e tocca "Attiva".
              </li>
            </ol>
          </>
        )}

        {variant === 'blocked' && (
          <>
            <div className="notifications-cta-title">
              Notifiche bloccate
            </div>
            <div className="notifications-cta-text">
              Hai bloccato le notifiche per CUT Catering. Per riattivarle vai
              nelle impostazioni del browser (sito → Notifiche → Consenti) e
              ricarica la pagina.
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default NotificationsCTA
